import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import useMealTypes from '../hooks/useMealTypes';
import { toLocalDatetimeInputValue } from '../utils/datetime';
import { prepJpeg } from '../utils/imagePrep';
import { uploadVisitBlob } from '../utils/visitPhotos';
import { locationLabel } from '../utils/locations';
import VisitDishRow from './VisitDishRow';

const MAX_PHOTOS = 5;

function EditVisitModal({ show, onHide, visit, onVisitUpdated }) {
  const mealTypes = useMealTypes();
  const [visitDate, setVisitDate] = useState('');
  const [mealType, setMealType] = useState('');
  const [rating, setRating] = useState(5);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [hoveredStar, setHoveredStar] = useState(0);
  // #120: add/edit/remove the dishes on this visit. `dishes` are editor rows
  // (VisitDishRow shape); only sent to the backend if the user touched them, so
  // an unrelated edit (notes/date) never rebuilds a public visit's reviews.
  const [dishes, setDishes] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [dishesDirty, setDishesDirty] = useState(false);
  // #168/#169: which location the visit was at. Add has offered this since #41;
  // Edit couldn't, because VisitUpdate had no location_id — so a visit logged as
  // "Not specified" (or at the wrong branch) was permanently unattributable.
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');
  // Private visit photos (R2). View shipped in #92; web upload/delete added here.
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const fileInputRef = useRef(null);
  const { token } = useAuth();

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  // Pre-populate form when visit changes
  useEffect(() => {
    if (visit) {
      setVisitDate(toLocalDatetimeInputValue(visit.visit_date));
      setMealType(visit.meal_type || '');
      setRating(visit.rating || 5);
      setNotes(visit.notes || '');
      setLocationId(visit.location_id ? String(visit.location_id) : '');
      // Seed the dish editor from the visit's dishes (#120). A menu-item dish
      // maps to its select value; an off-menu/catalog dish maps to "__other__"
      // with the typed name (#119) editable and its catalog match linked.
      setDishes((visit.dishes || []).map((d, i) => ({
        uid: `d-${d.id ?? 'x'}-${i}`,
        menuItemId: d.menu_item_id
          ? String(d.menu_item_id)
          : ((d.food_id || d.dish_name || d.custom_name) ? '__other__' : ''),
        dishNameText: d.custom_name || d.dish_name || (d.food_id ? (d.food_name || '') : ''),
        matchedFood: d.food_id ? { id: d.food_id, name: d.food_name || d.custom_name || 'Dish' } : null,
        rating: d.rating || 0,
      })));
      setDishesDirty(false);
    }
  }, [visit]);

  // Free staged dish-photo previews when the modal closes without saving, so
  // un-uploaded object URLs don't leak (#120/#115).
  useEffect(() => {
    if (!show) {
      dishes.forEach((d) => (d.photos || []).forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // The restaurant's menu items, so a dish can be picked from the menu (#120).
  useEffect(() => {
    if (!show || !visit) { setMenuItems([]); return; }
    let cancelled = false;
    // #260: owner-aware endpoint — editing a visit to your OWN private
    // restaurant must still offer its dishes, so send the token or the menu
    // picker comes back empty. Same shape as the locations fetch below (#248).
    fetch(`${API_BASE_URL}/foods/menu/restaurant/${visit.restaurant_id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => { if (!cancelled) setMenuItems(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setMenuItems([]); });
    return () => { cancelled = true; };
  }, [show, visit, API_BASE_URL, token]);

  // The restaurant's locations, so the visit can be pinned to one (or corrected)
  // after the fact (#168/#169). Only offered for multi-location restaurants — a
  // single location is auto-filled server-side at create time.
  useEffect(() => {
    if (!show || !visit) { setLocations([]); return; }
    let cancelled = false;
    // #248: owner-aware endpoint — a visit to your OWN private restaurant must
    // still offer its locations, so send the token or the picker comes back empty.
    fetch(`${API_BASE_URL}/restaurants/${visit.restaurant_id}/locations/`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => { if (!cancelled) setLocations(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setLocations([]); });
    return () => { cancelled = true; };
  }, [show, visit, API_BASE_URL, token]);

  // Lazy-load the visit's photos when the modal opens (owner-gated GET, returns
  // short-lived presigned URLs). One call per opened visit — no list fan-out.
  useEffect(() => {
    if (!show || !visit || !token) {
      setPhotos([]);
      return;
    }
    let cancelled = false;
    setPhotosLoading(true);
    fetch(`${API_BASE_URL}/photos/?visit_id=${visit.id}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (!cancelled) setPhotos(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setPhotos([]);
      })
      .finally(() => {
        if (!cancelled) setPhotosLoading(false);
      });
    return () => { cancelled = true; };
  }, [show, visit, token, API_BASE_URL]);

  // Upload picked images to this visit: presign → PUT to R2 → record. The PUT
  // goes browser→R2 directly, so R2 must have a CORS policy allowing PUT from
  // this origin (a Cloudflare/R2 config step, separate from this code).
  const handleAddPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (e.target) e.target.value = '';   // allow re-picking the same file
    if (!files.length || !token || !visit) return;
    setPhotoBusy(true);
    setPhotoError('');
    try {
      let current = photos.slice();
      for (const file of files) {
        if (current.length >= MAX_PHOTOS) break;
        const jpeg = await prepJpeg(file);

        const presignRes = await fetch(`${API_BASE_URL}/photos/presign`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ visit_id: visit.id, content_type: 'image/jpeg', ext: 'jpg' }),
        });
        if (!presignRes.ok) throw new Error('Could not start the upload');
        const { upload_url, r2_key } = await presignRes.json();

        const putRes = await fetch(upload_url, {
          method: 'PUT',
          headers: { 'Content-Type': 'image/jpeg' },
          body: jpeg,
        });
        if (!putRes.ok) throw new Error('Upload to storage failed');

        const recordRes = await fetch(`${API_BASE_URL}/photos/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ visit_id: visit.id, r2_key, content_type: 'image/jpeg' }),
        });
        if (!recordRes.ok) throw new Error('Could not save the photo');
        current = [...current, await recordRes.json()];
        setPhotos(current);
      }
    } catch (err) {
      setPhotoError(err.message || 'Photo upload failed. Please try again.');
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!token) return;
    setPhotoBusy(true);
    setPhotoError('');
    try {
      const res = await fetch(`${API_BASE_URL}/photos/${photoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok && res.status !== 204) throw new Error('Could not delete the photo');
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
    } catch (err) {
      setPhotoError(err.message || 'Delete failed. Please try again.');
    } finally {
      setPhotoBusy(false);
    }
  };

  // --- dish editor (#120) ---------------------------------------------------
  const updateDish = (uid, patch) => {
    setDishesDirty(true);
    setDishes((prev) => prev.map((d) => (d.uid === uid ? { ...d, ...patch } : d)));
  };
  const addDish = () => {
    setDishesDirty(true);
    setDishes((prev) => [...prev, {
      uid: `new-${Date.now()}-${prev.length}`,
      menuItemId: '', dishNameText: '', matchedFood: null, rating: 0,
    }]);
  };
  const removeDish = (uid) => {
    setDishesDirty(true);
    setDishes((prev) => prev.filter((d) => d.uid !== uid));
  };

  // Photos of a NEW dish added on edit (#120): stage locally now, upload after
  // Save once the dish has a server id to attribute to. Shares the per-visit cap
  // with the visit's existing photos. Staging marks dishes dirty so the dishes
  // payload is sent (and the response returns the ids to attribute to).
  const stagedDishPhotoCount = () => dishes.reduce((n, d) => n + (d.photos ? d.photos.length : 0), 0);
  const photoRoom = () => Math.max(0, MAX_PHOTOS - photos.length - stagedDishPhotoCount());
  const addDishPhotos = (uid, files) => {
    if (!files.length) return;
    setPhotoError('');
    setDishesDirty(true);
    setDishes((prev) => {
      const room = MAX_PHOTOS - photos.length - prev.reduce((n, d) => n + (d.photos ? d.photos.length : 0), 0);
      const added = files.slice(0, Math.max(0, room)).map((file) => ({ file, previewUrl: URL.createObjectURL(file) }));
      return prev.map((d) => (d.uid === uid ? { ...d, photos: [...(d.photos || []), ...added] } : d));
    });
  };
  const removeDishPhoto = (uid, idx) => {
    setDishes((prev) => prev.map((d) => {
      if (d.uid !== uid) return d;
      const target = (d.photos || [])[idx];
      if (target && target.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return { ...d, photos: (d.photos || []).filter((_, i) => i !== idx) };
    }));
  };

  // Resolve one editor row to its #110 dish payload, or null if it names nothing.
  const dishToPayload = (d) => {
    const dishRating = d.rating || null;
    if (d.menuItemId === '__other__') {
      if (d.matchedFood) {
        const typed = (d.dishNameText || '').trim();
        const custom_name = (typed && typed !== d.matchedFood.name) ? typed : null;  // #119
        return { food_id: d.matchedFood.id, menu_item_id: null, dish_name: null, custom_name, rating: dishRating };
      }
      const nm = (d.dishNameText || '').trim();
      return nm ? { food_id: null, menu_item_id: null, dish_name: nm, rating: dishRating } : null;
    }
    if (d.menuItemId) {
      const mi = menuItems.find((m) => String(m.id) === String(d.menuItemId));
      return { food_id: mi?.food_id || null, menu_item_id: mi ? Number(mi.id) : Number(d.menuItemId), dish_name: null, rating: dishRating };
    }
    return null; // "Not specified" / empty row
  };
  // Editor rows that resolve to a real payload, paired with their source dish —
  // in the SAME order the backend assigns positions, so staged photos map to the
  // returned visit_dish_id by position (mirrors the create flow).
  const buildDishEntries = () =>
    dishes.map((d) => ({ payload: dishToPayload(d), src: d })).filter((e) => e.payload);
  const buildDishesPayload = () => buildDishEntries().map((e) => e.payload);

  // Upload each dish's staged photos to the saved visit, attributed by position
  // to its returned visit_dish_id (#115). Best-effort. Returns whether any dish
  // photo landed (so a public visit can opt them in).
  const uploadStagedDishPhotos = async (responseDishes) => {
    const posToId = {};
    (responseDishes || []).forEach((d) => { posToId[d.position] = d.id; });
    const entries = buildDishEntries();
    let uploadedDishPhoto = false;
    let failed = false;
    for (let i = 0; i < entries.length; i++) {
      const vdid = posToId[i];
      for (const p of (entries[i].src.photos || [])) {
        if (!p.file) continue;
        try {
          const jpeg = await prepJpeg(p.file);
          await uploadVisitBlob(API_BASE_URL, token, visit.id, jpeg, vdid ?? null);
          if (vdid != null) uploadedDishPhoto = true;
        } catch { failed = true; }
      }
    }
    return { uploadedDishPhoto, failed };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const updateData = {
        visit_date: new Date(visitDate).toISOString(),
        meal_type: mealType || null,
        rating: rating,
        notes: notes || null,
      };
      // Only send dishes when the user actually edited them (#120) — avoids
      // needlessly rebuilding a public visit's reviews on an unrelated edit.
      if (dishesDirty) {
        updateData.dishes = buildDishesPayload();
      }
      // Only send location_id when it actually changed (#169): an explicit null
      // CLEARS the attribution server-side, so an unrelated edit must not carry
      // one. Sent as null when the user picks "Not specified" on purpose.
      const currentLocationId = visit.location_id ? String(visit.location_id) : '';
      if (locationId !== currentLocationId) {
        updateData.location_id = locationId ? Number(locationId) : null;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/visits/${visit.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to update visit';
        try {
          const errorData = await response.json();
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
          } else {
            errorMessage = JSON.stringify(errorData.detail || errorData);
          }
        } catch (e) {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Upload any photos staged on a dish, now that Save returned each dish's
      // id (#120/#115). If the visit is public, opt the new dish photos into the
      // review (dish_photos_only keeps un-attributed shots private).
      if (stagedDishPhotoCount() > 0) {
        const { uploadedDishPhoto, failed } = await uploadStagedDishPhotos(data.dishes);
        if (failed) {
          setPhotoError('Visit saved, but a dish photo didn’t upload. You can retry from your profile.');
        }
        if (uploadedDishPhoto && visit.is_public) {
          try {
            await fetch(`${API_BASE_URL}/api/v1/visits/${visit.id}/share-photos?share=true&dish_photos_only=true`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` },
            });
          } catch { /* soft-fail: photo is uploaded, just not public yet */ }
        }
        // Clear staged previews so a re-open doesn't double-count them.
        dishes.forEach((d) => (d.photos || []).forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl)));
        setDishes((prev) => prev.map((d) => ({ ...d, photos: [] })));
      }

      setSuccess('Visit updated successfully!');

      // Notify parent component
      if (onVisitUpdated) {
        onVisitUpdated(data);
      }

      // Close modal after 1 second
      setTimeout(() => {
        onHide();
        setSuccess('');
      }, 1000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = () => {
    return (
      <div className="star-rating mb-3">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`star ${star <= (hoveredStar || rating) ? 'filled' : ''}`}
            onClick={() => setRating(star)}
            onMouseEnter={() => setHoveredStar(star)}
            onMouseLeave={() => setHoveredStar(0)}
            style={{
              cursor: 'pointer',
              fontSize: '2rem',
              color: star <= (hoveredStar || rating) ? '#ffc107' : '#e4e5e9',
              marginRight: '0.25rem',
            }}
          >
            ★
          </span>
        ))}
        <span className="ms-2 text-muted">({rating} star{rating !== 1 ? 's' : ''})</span>
      </div>
    );
  };

  if (!visit) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit Visit to {visit.restaurant_name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

        <div className="mb-4">
          <Form.Label className="d-block">Your Photos</Form.Label>
          {photosLoading ? (
            <div className="text-muted small">Loading photos…</div>
          ) : (
            <>
              <div className="d-flex flex-wrap gap-2">
                {photos.map((photo) => (
                  <div key={photo.id} style={{ position: 'relative' }}>
                    <a
                      href={photo.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={photo.caption || 'View full size'}
                    >
                      <img
                        src={photo.url}
                        alt={photo.caption || 'Visit photo'}
                        style={{
                          width: '96px',
                          height: '96px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          border: '1px solid #dee2e6',
                        }}
                      />
                    </a>
                    <Button
                      variant="dark"
                      size="sm"
                      onClick={() => handleDeletePhoto(photo.id)}
                      disabled={photoBusy}
                      title="Delete photo"
                      style={{
                        position: 'absolute', top: '2px', right: '2px',
                        padding: '0 6px', lineHeight: 1.4, borderRadius: '50%', opacity: 0.85,
                      }}
                    >
                      ×
                    </Button>
                  </div>
                ))}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleAddPhotos}
                style={{ display: 'none' }}
              />
              <div className="d-flex align-items-center gap-2 mt-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  disabled={photoBusy || photos.length >= MAX_PHOTOS}
                >
                  {photos.length === 0 ? 'Add a photo' : `Add photo (${photos.length}/${MAX_PHOTOS})`}
                </Button>
                {photoBusy && <span className="text-muted small">Uploading…</span>}
              </div>

              {photoError && <div className="text-danger small mt-1">{photoError}</div>}
              <div className="text-muted small mt-1">
                Private to you. Photo metadata (including location) is removed before upload.
              </div>
            </>
          )}
        </div>

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Visit Date</Form.Label>
            <Form.Control
              type="datetime-local"
              value={visitDate}
              onChange={(e) => setVisitDate(e.target.value)}
              required
            />
          </Form.Group>

          {/* #168/#169: pin (or correct) which location this visit was at. Only
              for multi-location restaurants, matching Add. */}
          {locations.length > 1 && (
            <Form.Group className="mb-3">
              <Form.Label>Which location? (Optional)</Form.Label>
              <Form.Select
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
              >
                <option value="">Not specified</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>{locationLabel(loc)}</option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Meal Type (Optional)</Form.Label>
            <Form.Select
              value={mealType}
              onChange={(e) => setMealType(e.target.value)}
            >
              <option value="">Select meal type...</option>
              {mealTypes.map((m) => (
                <option key={m.slug} value={m.slug}>{m.display_name}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Overall experience</Form.Label>
            {renderStars()}
            <Form.Text className="text-muted d-block">
              Service, ambience, value — separate from how each dish was.
            </Form.Text>
          </Form.Group>

          {/* #120: add/edit/remove the dishes on this visit. Each dish carries
              its own rating; off-menu dishes keep the typed name (#119). */}
          <Form.Group className="mb-3">
            <Form.Label>Dishes</Form.Label>
            {dishes.map((d, i) => (
              <VisitDishRow
                key={d.uid}
                index={i}
                dish={d}
                menuItems={menuItems}
                apiBaseUrl={API_BASE_URL}
                onChange={(patch) => updateDish(d.uid, patch)}
                onRemove={() => removeDish(d.uid)}
                onAddPhotos={(files) => addDishPhotos(d.uid, files)}
                onRemovePhoto={(idx) => removeDishPhoto(d.uid, idx)}
                photoRoom={photoRoom()}
              />
            ))}
            <Button variant="outline-secondary" size="sm" onClick={addDish} type="button">
              + Add a dish
            </Button>
            {dishes.length === 0 && (
              <Form.Text className="text-muted d-block mt-1">
                No dishes logged yet — add what you had.
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Notes (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What did you order? How was the service?"
            />
          </Form.Group>

          <div className="d-flex gap-2">
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update Visit'}
            </Button>
            <Button variant="secondary" onClick={onHide} disabled={loading}>
              Cancel
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default EditVisitModal;
